import { UserRole } from '../types';

// Icon type - mapped from Lucide React icon names
export type IconName =
  | 'Users' | 'BarChart3' | 'Settings' | 'LogOut' | 'Zap'
  | 'Home' | 'Calendar' | 'MessageSquare' | 'Star' | 'BookOpen' | 'Navigation'
  | 'DollarSign' | 'Banknote' | 'Scale' | 'Shield' | 'Palette' | 'Radio';

export interface MenuItem {
  id: string;
  label: string;
  iconName: IconName;
  path: string;
  visible: boolean;
  children?: MenuItem[];
}

export interface RoleConfig {
  role: UserRole;
  displayName: string;
  dashboardPath: string;
  menuItems: MenuItem[];
  description: string;
}

export const ROLE_CONFIGS: Record<UserRole, RoleConfig> = {
  SUPER_ADMIN: {
    role: 'SUPER_ADMIN',
    displayName: 'Super Admin',
    dashboardPath: '/dashboard/super-admin',
    description: 'Full platform control and system administration',
    menuItems: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        iconName: 'Home',
        path: '/dashboard/super-admin',
        visible: true,
      },
      {
        id: 'organizations',
        label: 'Organizations',
        iconName: 'Navigation',
        path: '/dashboard/super-admin/organizations',
        visible: true,
      },
      {
        id: 'system-settings',
        label: 'System Settings',
        iconName: 'Zap',
        path: '/dashboard/super-admin/system-settings',
        visible: true,
      },
      {
        id: 'feature-flags',
        label: 'Feature Flags',
        iconName: 'Zap',
        path: '/dashboard/super-admin/feature-flags',
        visible: true,
      },
      {
        id: 'debug',
        label: 'Debug',
        iconName: 'Settings',
        path: '/dashboard/super-admin/debug',
        visible: true,
      },
      {
        id: 'backups',
        label: 'Backups',
        iconName: 'BookOpen',
        path: '/dashboard/super-admin/backups',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/settings/profile',
        visible: true,
      },
    ],
  },

  PLATFORM_ADMIN: {
    role: 'PLATFORM_ADMIN',
    displayName: 'Platform Admin',
    dashboardPath: '/dashboard/platform-admin',
    description: 'Platform overview and management',
    menuItems: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        iconName: 'Home',
        path: '/dashboard/platform-admin',
        visible: true,
      },
      {
        id: 'listings',
        label: 'Listing Review',
        iconName: 'Star',
        path: '/dashboard/platform-admin/listings',
        visible: true,
      },
      {
        id: 'experience-review',
        label: 'Experience Review',
        iconName: 'BookOpen',
        path: '/dashboard/platform-admin/experience-review',
        visible: true,
      },
      {
        id: 'properties',
        label: 'Properties',
        iconName: 'Navigation',
        path: '/dashboard/platform-admin/properties',
        visible: true,
      },
      {
        id: 'bookings',
        label: 'Bookings',
        iconName: 'Calendar',
        path: '/dashboard/platform-admin/bookings',
        visible: true,
      },
      {
        id: 'users',
        label: 'Users',
        iconName: 'Users',
        path: '/dashboard/platform-admin/users',
        visible: true,
      },
      {
        id: 'organizations',
        label: 'Organizations',
        iconName: 'Navigation',
        path: '/dashboard/platform-admin/organizations',
        visible: true,
      },
      {
        id: 'platform-staff',
        label: 'Platform Staff',
        iconName: 'Shield',
        path: '/dashboard/platform-admin/platform-staff',
        visible: true,
      },
      {
        id: 'disputes',
        label: 'Disputes',
        iconName: 'Scale',
        path: '/dashboard/platform-admin/disputes',
        visible: true,
      },
      {
        id: 'payments',
        label: 'Payments',
        iconName: 'DollarSign',
        path: '/dashboard/platform-admin/payments',
        visible: true,
      },
      {
        id: 'appearance',
        label: 'Appearance',
        iconName: 'Palette',
        path: '/dashboard/platform-admin/appearance',
        visible: true,
      },
      {
        id: 'channel-managers',
        label: 'Channel Managers',
        iconName: 'Radio',
        path: '/dashboard/platform-admin/channel-managers',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/settings/profile',
        visible: true,
      },
    ],
  },

  ORG_ADMIN: {
    role: 'ORG_ADMIN',
    displayName: 'Organization Admin',
    dashboardPath: '/dashboard/org-admin',
    description: 'Manage your organization and properties',
    menuItems: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        iconName: 'Home',
        path: '/dashboard/org-admin',
        visible: true,
      },
      {
        id: 'properties',
        label: 'Properties',
        iconName: 'Navigation',
        path: '/dashboard/org-admin/properties',
        visible: true,
      },
      {
        id: 'team',
        label: 'Team',
        iconName: 'Users',
        path: '/dashboard/org-admin/team',
        visible: true,
      },
      {
        id: 'experiences-new',
        label: 'Add Experience',
        iconName: 'Star',
        path: '/dashboard/experiences/new',
        visible: true,
      },
      {
        id: 'bookings',
        label: 'Bookings',
        iconName: 'Calendar',
        path: '/dashboard/org-admin/bookings',
        visible: true,
      },
      {
        id: 'guests',
        label: 'Guests',
        iconName: 'Users',
        path: '/dashboard/org-admin/guests',
        visible: true,
      },
      {
        id: 'reviews',
        label: 'Reviews',
        iconName: 'Star',
        path: '/dashboard/org-admin/reviews',
        visible: true,
      },
      {
        id: 'policies',
        label: 'Policies',
        iconName: 'Scale',
        path: '/dashboard/org-admin/policies',
        visible: true,
      },
      {
        id: 'audit-log',
        label: 'Audit Log',
        iconName: 'Shield',
        path: '/dashboard/org-admin/audit-log',
        visible: true,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        iconName: 'BarChart3',
        path: '/dashboard/org-admin/analytics',
        visible: true,
      },
      {
        id: 'commission',
        label: 'Commission',
        iconName: 'DollarSign',
        path: '/dashboard/org-admin/commission',
        visible: true,
      },
      {
        id: 'payout-settings',
        label: 'Payout Settings',
        iconName: 'Banknote',
        path: '/dashboard/org-admin/payout-settings',
        visible: true,
      },
      {
        id: 'calendar-sync',
        label: 'Calendar Sync',
        iconName: 'Zap',
        path: '/dashboard/org-admin/calendar-sync',
        visible: true,
      },
      {
        id: 'channel-sync',
        label: 'Channel / OTA Sync',
        iconName: 'Zap',
        path: '/dashboard/org-admin/channel-sync',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/dashboard/org-admin/settings',
        visible: true,
      },
    ],
  },

  ORG_MANAGER: {
    role: 'ORG_MANAGER',
    displayName: 'Organization Manager',
    dashboardPath: '/dashboard/org-manager',
    description: 'Manage assigned properties',
    menuItems: [
      {
        id: 'my-properties',
        label: 'My Properties',
        iconName: 'Navigation',
        path: '/dashboard/org-manager/properties',
        visible: true,
      },
      {
        id: 'experiences-new',
        label: 'Add Experience',
        iconName: 'Star',
        path: '/dashboard/experiences/new',
        visible: true,
      },
      {
        id: 'bookings',
        label: 'Bookings',
        iconName: 'Calendar',
        path: '/dashboard/org-manager/bookings',
        visible: true,
      },
      {
        id: 'guests',
        label: 'Guests',
        iconName: 'Users',
        path: '/dashboard/org-manager/guests',
        visible: true,
      },
      {
        id: 'messages',
        label: 'Messages',
        iconName: 'MessageSquare',
        path: '/dashboard/org-manager/messages',
        visible: true,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        iconName: 'BarChart3',
        path: '/dashboard/org-manager/analytics',
        visible: true,
      },
      {
        id: 'compliance',
        label: 'Compliance',
        iconName: 'Shield',
        path: '/dashboard/org-manager/compliance',
        visible: true,
      },
      {
        id: 'calendar-sync',
        label: 'Calendar Sync',
        iconName: 'Zap',
        path: '/dashboard/org-manager/calendar-sync',
        visible: true,
      },
      {
        id: 'channel-sync',
        label: 'Channel / OTA Sync',
        iconName: 'Radio',
        path: '/dashboard/org-manager/channel-sync',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/dashboard/org-manager/settings',
        visible: true,
      },
    ],
  },

  ORG_STAFF: {
    role: 'ORG_STAFF',
    displayName: 'Organization Staff',
    dashboardPath: '/dashboard/org-staff',
    description: 'Manage tasks and schedules',
    menuItems: [
      {
        id: 'my-tasks',
        label: 'My Tasks',
        iconName: 'Calendar',
        path: '/dashboard/org-staff/tasks',
        visible: true,
      },
      {
        id: 'schedule',
        label: 'Schedule',
        iconName: 'Calendar',
        path: '/dashboard/org-staff/schedule',
        visible: true,
      },
      {
        id: 'properties',
        label: 'Properties',
        iconName: 'Navigation',
        path: '/dashboard/org-staff/properties',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/dashboard/org-staff/settings',
        visible: true,
      },
    ],
  },

  PLATFORM_STAFF: {
    role: 'PLATFORM_STAFF',
    displayName: 'Platform Staff',
    dashboardPath: '/dashboard/platform-staff',
    description: 'Onboard organizations and add properties on their behalf',
    menuItems: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        iconName: 'Home',
        path: '/dashboard/platform-staff',
        visible: true,
      },
      {
        id: 'organizations',
        label: 'Organizations',
        iconName: 'Navigation',
        path: '/dashboard/platform-staff/organizations',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/settings/profile',
        visible: true,
      },
    ],
  },

  GUEST: {
    role: 'GUEST',
    displayName: 'Guest',
    dashboardPath: '/dashboard/guest',
    description: 'Browse and book properties',
    menuItems: [
      {
        id: 'explore',
        label: 'Explore',
        iconName: 'Navigation',
        path: '/dashboard/guest/explore',
        visible: true,
      },
      {
        id: 'my-bookings',
        label: 'My Bookings',
        iconName: 'Calendar',
        path: '/dashboard/guest/bookings',
        visible: true,
      },
      {
        id: 'my-trips',
        label: 'My Trips',
        iconName: 'Zap',
        path: '/dashboard/guest/trips',
        visible: true,
      },
      {
        id: 'favorites',
        label: 'Favorites',
        iconName: 'Star',
        path: '/dashboard/guest/favorites',
        visible: true,
      },
      {
        id: 'messages',
        label: 'Messages',
        iconName: 'MessageSquare',
        path: '/dashboard/guest/messages',
        visible: true,
      },
      {
        id: 'reviews',
        label: 'Reviews',
        iconName: 'Star',
        path: '/dashboard/guest/reviews',
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        iconName: 'Settings',
        path: '/dashboard/guest/settings',
        visible: true,
      },
    ],
  },
};

/**
 * Get role configuration by user role
 */
export function getRoleConfig(role: UserRole): RoleConfig {
  return ROLE_CONFIGS[role];
}

/**
 * Get all menu items for a given role
 */
export function getRoleMenuItems(role: UserRole): MenuItem[] {
  return ROLE_CONFIGS[role].menuItems.filter((item) => item.visible);
}

/**
 * Get dashboard path for a given role
 */
export function getDashboardPath(role: UserRole): string {
  return ROLE_CONFIGS[role].dashboardPath;
}

/**
 * Get display name for a given role
 */
export function getRoleDisplayName(role: UserRole): string {
  return ROLE_CONFIGS[role].displayName;
}
